import React from "react";
import {
  Button,
  Card,
  Form,
  Container,
  Row,
  Col,
  FormGroup,
} from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

// Schema for yup
const couponSchema = Yup.object().shape({
  code: Yup.string()
    .min(3, "Code must have at least 3 characters")
    .max(30, "Code can't be longer than 30 characters")
    // any symbol is allowed, codes are matched by exact equality and never used
    // in a query pattern, so the only thing that would break is whitespace
    .matches(/^\S+$/, "Code can't contain spaces")
    .required("Code field is required"),
  title: Yup.string()
    .min(2, "Title must have at least 2 characters")
    .max(100, "Title can't be longer than 100 characters")
    .trim("Spaces are not allowed")
    .required("Title field is required"),
  discountType: Yup.string()
    .oneOf(["fixed", "percentage"])
    .required("Discount type field is required"),
  discountValue: Yup.number()
    .typeError("Discount value must be a number")
    .positive("Discount value must be greater than 0")
    .required("Discount value field is required")
    .when("discountType", {
      is: "percentage",
      then: (schema) => schema.max(100, "A percentage can't be above 100"),
    }),
  maxDiscountAmount: Yup.number()
    .typeError("Max discount must be a number")
    .min(0, "Max discount can't be negative"),
  minOrderAmount: Yup.number()
    .typeError("Minimum visit price must be a number")
    .min(0, "Minimum visit price can't be negative"),
  usageLimit: Yup.number()
    .typeError("Total usage limit must be a number")
    .integer("Total usage limit must be a whole number")
    .min(0, "Total usage limit can't be negative"),
  usageLimitPerUser: Yup.number()
    .typeError("Usage limit per patient must be a number")
    .integer("Usage limit per patient must be a whole number")
    .min(1, "Usage limit per patient must be at least 1")
    .required("Usage limit per patient field is required"),
  startDate: Yup.date()
    .typeError("Start date field is required")
    .required("Start date field is required"),
  endDate: Yup.date()
    .typeError("End date field is required")
    .required("End date field is required")
    .min(Yup.ref("startDate"), "End date can't be before the start date"),
});

/**
 * Spells out in plain words what the two usage limits add up to, because
 * "total" and "per patient" are easy to mix up and the difference only shows
 * itself after the first patient has redeemed the coupon.
 */
const usageSummary = ({ usageLimit, usageLimitPerUser, firstTimeOnly }) => {
  const total = Number(usageLimit) || 0;
  const perUser = Number(usageLimitPerUser) || 1;

  const who = firstTimeOnly
    ? "Only patients who have never paid for a visit"
    : "Any patient";
  const times = perUser === 1 ? "once" : `up to ${perUser} times`;

  if (total > 0) {
    return `${who} can use this coupon ${times} — but it stops working for
      everyone after ${total} total redemption${total === 1 ? "" : "s"}.`;
  }

  return `${who} can use this coupon ${times}. There is no cap on how many
    patients in total.`;
};

const FieldError = ({ formik, name }) =>
  formik.touched[name] && formik.errors[name] ? (
    <span>
      <label className="pl-1 text-danger">{formik.errors[name]}</label>
    </span>
  ) : null;

const CouponForm = ({ heading, initialValues, onSubmit }) => {
  const formik = useFormik({
    initialValues,
    validationSchema: couponSchema,
    enableReinitialize: true,
    onSubmit,
  });

  return (
    <div className="pt-2 pt-md-3 cms-updated-style">
      <Container fluid>
        <Row>
          <Col md="12">
            <Card className="filter-card card">
              <Card.Header>
                <div className="d-flex align-items-center justify-content-between table-head">
                  <Card.Title className="text-white" as="h4">
                    {heading}
                  </Card.Title>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col>
                    <Form onSubmit={formik.handleSubmit}>
                      <Form.Group className="mb-3" controlId="formCouponCode">
                        <Form.Label>
                          Coupon Code <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="code"
                          placeholder="e.g WELCOME5"
                          onChange={(e) =>
                            formik.setFieldValue(
                              "code",
                              e.target.value.toUpperCase()
                            )
                          }
                          value={formik.values.code}
                          onBlur={formik.handleBlur}
                        />
                        <Form.Text className="text-muted">
                          This is what the patient types at checkout.
                        </Form.Text>
                        <FieldError formik={formik} name="code" />
                      </Form.Group>

                      <Form.Group className="mb-3" controlId="formCouponTitle">
                        <Form.Label>
                          Title <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          placeholder="e.g New patient welcome offer"
                          onChange={formik.handleChange}
                          value={formik.values.title}
                          onBlur={formik.handleBlur}
                        />
                        <FieldError formik={formik} name="title" />
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3"
                            controlId="formDiscountType"
                          >
                            <Form.Label>
                              Discount Type{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <select
                              name="discountType"
                              className="form-control"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.discountType}
                            >
                              <option value="fixed">Fixed Amount ($)</option>
                              <option value="percentage">
                                Percentage (%)
                              </option>
                            </select>
                            <FieldError formik={formik} name="discountType" />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3"
                            controlId="formDiscountValue"
                          >
                            <Form.Label>
                              Discount Value{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              name="discountValue"
                              placeholder={
                                formik.values.discountType === "percentage"
                                  ? "e.g 10 for 10% off"
                                  : "e.g 5 for $5 off"
                              }
                              onChange={formik.handleChange}
                              value={formik.values.discountValue}
                              onBlur={formik.handleBlur}
                            />
                            <FieldError formik={formik} name="discountValue" />
                          </Form.Group>
                        </Col>
                      </Row>

                      {formik.values.discountType === "percentage" ? (
                        <Form.Group
                          className="mb-3"
                          controlId="formMaxDiscount"
                        >
                          <Form.Label>Maximum Discount Amount ($)</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            name="maxDiscountAmount"
                            placeholder="0 for no cap"
                            onChange={formik.handleChange}
                            value={formik.values.maxDiscountAmount}
                            onBlur={formik.handleBlur}
                          />
                          <Form.Text className="text-muted">
                            Caps how much a percentage coupon can take off. Use
                            0 for no cap.
                          </Form.Text>
                          <FieldError
                            formik={formik}
                            name="maxDiscountAmount"
                          />
                        </Form.Group>
                      ) : null}

                      <Form.Group className="mb-3" controlId="formMinOrder">
                        <Form.Label>Minimum Visit Price ($)</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          name="minOrderAmount"
                          placeholder="0 for no minimum"
                          onChange={formik.handleChange}
                          value={formik.values.minOrderAmount}
                          onBlur={formik.handleBlur}
                        />
                        <Form.Text className="text-muted">
                          The coupon only applies when the visit costs at least
                          this much.
                        </Form.Text>
                        <FieldError formik={formik} name="minOrderAmount" />
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3"
                            controlId="formStartDate"
                          >
                            <Form.Label>
                              Start Date <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              className="promo-code-dates"
                              type="date"
                              name="startDate"
                              onChange={formik.handleChange}
                              value={formik.values.startDate}
                              onBlur={formik.handleBlur}
                            />
                            <FieldError formik={formik} name="startDate" />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="formEndDate">
                            <Form.Label>
                              End Date <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              className="promo-code-dates"
                              type="date"
                              name="endDate"
                              min={formik.values.startDate || undefined}
                              onChange={formik.handleChange}
                              value={formik.values.endDate}
                              onBlur={formik.handleBlur}
                            />
                            <FieldError formik={formik} name="endDate" />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3"
                            controlId="formUsageLimit"
                          >
                            <Form.Label>
                              Total Redemptions, All Patients Combined
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="usageLimit"
                              placeholder="0 for unlimited"
                              onChange={formik.handleChange}
                              value={formik.values.usageLimit}
                              onBlur={formik.handleBlur}
                            />
                            <Form.Text className="text-muted">
                              Keep this 0 unless you are running a limited batch.
                              Setting it to 1 retires the coupon after the very
                              first patient uses it.
                            </Form.Text>
                            <FieldError formik={formik} name="usageLimit" />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3"
                            controlId="formUsageLimitPerUser"
                          >
                            <Form.Label>
                              Redemptions Per Patient{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="usageLimitPerUser"
                              placeholder="e.g 1"
                              onChange={formik.handleChange}
                              value={formik.values.usageLimitPerUser}
                              onBlur={formik.handleBlur}
                            />
                            <Form.Text className="text-muted">
                              How many times one patient may use it. This is the
                              field you usually want set to 1.
                            </Form.Text>
                            <FieldError
                              formik={formik}
                              name="usageLimitPerUser"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <div className="alert alert-info py-2 mb-3">
                            {usageSummary(formik.values)}
                          </div>
                        </Col>
                      </Row>

                      <FormGroup>
                        <Row>
                          <Col md={4}>
                            <label className="label-font">
                              New Patients Only
                            </label>
                          </Col>
                          <Col md={8} className="check-inline d-flex">
                            <label className="right-label-radio mr-3 mb-2">
                              Yes
                              <input
                                name="firstTimeOnly"
                                type="radio"
                                checked={formik.values.firstTimeOnly}
                                onChange={() =>
                                  formik.setFieldValue("firstTimeOnly", true)
                                }
                              />
                              <span className="checkmark"></span>
                            </label>
                            <label className="right-label-radio mr-3 mb-2">
                              No
                              <input
                                name="firstTimeOnlyNo"
                                type="radio"
                                checked={!formik.values.firstTimeOnly}
                                onChange={() =>
                                  formik.setFieldValue("firstTimeOnly", false)
                                }
                              />
                              <span className="checkmark"></span>
                            </label>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Text className="text-muted mb-3 d-block">
                              When set to Yes, only a patient who has never paid
                              for a visit before can use this coupon.
                            </Form.Text>
                          </Col>
                        </Row>
                      </FormGroup>

                      <FormGroup>
                        <Row>
                          <Col md={4}>
                            <label className="label-font">Status</label>
                          </Col>
                          <Col md={8} className="check-inline d-flex">
                            <label className="right-label-radio mr-3 mb-2">
                              Active
                              <input
                                name="status"
                                type="radio"
                                checked={formik.values.status}
                                onChange={() =>
                                  formik.setFieldValue("status", true)
                                }
                              />
                              <span className="checkmark"></span>
                            </label>
                            <label className="right-label-radio mr-3 mb-2">
                              Inactive
                              <input
                                name="status1"
                                type="radio"
                                checked={!formik.values.status}
                                onChange={() =>
                                  formik.setFieldValue("status", false)
                                }
                              />
                              <span className="checkmark"></span>
                            </label>
                          </Col>
                        </Row>
                      </FormGroup>

                      <Button className="btn-filled" type="submit">
                        Submit
                      </Button>
                    </Form>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CouponForm;
